import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useTenant } from '../../contexts/TenantContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { RichContentEditor } from '../../components/RichContentEditor';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  Plus, Trash2, Edit, Database, Save, 
  BedDouble, Utensils, Eye, Tag, Activity, DollarSign,
  FileText, CheckSquare, XSquare, AlertTriangle, CreditCard, Layers,
  Car, MapPin, Globe, Coins, Users
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

const QUOTE_MASTER_TYPES = [
  { value: 'INCLUSION', label: 'Inclusions', icon: CheckSquare },
  { value: 'EXCLUSION', label: 'Exclusions', icon: XSquare },
  { value: 'PAYMENT', label: 'Payment Terms', icon: CreditCard },
  { value: 'CANCELLATION', label: 'Cancellation Policy', icon: AlertTriangle },
  { value: 'TERMS', label: 'Terms & Conditions', icon: FileText },
];

const TAG_CATEGORIES = [
  { value: 'ROOM_VIEW', label: 'Room View' },
  { value: 'ROOM_TYPE', label: 'Room Type' },
  { value: 'AMENITIES', label: 'Amenities' },
];

const MastersManager = () => {
  const navigate = useNavigate();
  const { buildPath } = useTenant();
  
  // Rate Masters (from Hotel Rates)
  const [rateMasters, setRateMasters] = useState({
    roomTypes: [],
    mealPlans: [],
    bedTypes: [],
    viewTypes: [],
    features: [],
    suppTypes: [],
    ratePerTypes: [],
    vehicleTypes: [],
    transportTypes: [],
    states: [],
    placeTypes: [],
    currencies: [],
    supplierTypes: []
  });
  
  // Quote Masters (from Settings)
  const [quoteMasters, setQuoteMasters] = useState([]);
  const [masterDialogOpen, setMasterDialogOpen] = useState(false);
  const [editingMaster, setEditingMaster] = useState(null);
  const [masterForm, setMasterForm] = useState({
    master_type: 'INCLUSION',
    title: '',
    content: '',
    display_order: 0,
    is_default: true,
  });
  
  // Room Tags
  const [tags, setTags] = useState([]);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [tagForm, setTagForm] = useState({
    tag_category: 'ROOM_VIEW',
    tag_name: '',
    tag_icon: '',
  });
  
  // Category Bands
  const [bands, setBands] = useState([]);
  const [bandDialogOpen, setBandDialogOpen] = useState(false);
  const [editingBand, setEditingBand] = useState(null);
  const [bandForm, setBandForm] = useState({
    category_name: '',
    min_rate: '',
    max_rate: '',
    display_order: 0,
  });
  
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchAllData();
  }, []);
  
  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch each independently so one failure doesn't break all
      try {
        const rateRes = await api.get('/hotel-rates/masters');
        setRateMasters(rateRes.data || {});
      } catch (e) {
        console.error('Failed to load rate masters:', e);
      }
      
      try {
        const mastersRes = await api.get('/admin/masters');
        setQuoteMasters(mastersRes.data || []);
      } catch (e) {
        console.error('Failed to load quote masters:', e);
      }
      
      try {
        const tagsRes = await api.get('/admin/room-tags');
        setTags(tagsRes.data || []);
      } catch (e) {
        console.error('Failed to load room tags:', e);
      }
      
      try {
        const bandsRes = await api.get('/admin/category-bands');
        setBands(bandsRes.data || []);
      } catch (e) {
        console.error('Failed to load category bands:', e);
      }
      
    } catch (error) {
      console.error('Failed to load masters:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Rate Masters handlers
  const handleAddRateMaster = async (category, value) => {
    if (!value.trim()) return;
    try {
      await api.post('/hotel-rates/masters', { category, value: value.trim() });
      const res = await api.get('/hotel-rates/masters');
      setRateMasters(res.data);
      toast.success('Added successfully');
    } catch (error) {
      toast.error('Failed to add');
    }
  };
  
  const handleDeleteRateMaster = async (category, value) => {
    if (!confirm(`Delete "${value}"?`)) return;
    try {
      await api.delete('/hotel-rates/masters', { data: { category, value } });
      const res = await api.get('/hotel-rates/masters');
      setRateMasters(res.data);
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  // Quote Masters handlers
  const resetMasterForm = () => {
    setEditingMaster(null);
    setMasterForm({
      master_type: 'INCLUSION',
      title: '',
      content: '',
      display_order: 0,
      is_default: true,
    });
  };
  
  const handleMasterSubmit = async (e) => {
    e.preventDefault();
    if (!String(masterForm.content || '').trim()) {
      toast.error('Content is required');
      return;
    }
    try {
      if (editingMaster) {
        await api.put(`/admin/masters/${editingMaster.master_id}`, masterForm);
        toast.success('Updated');
      } else {
        await api.post('/admin/masters', masterForm);
        toast.success('Created');
      }
      setMasterDialogOpen(false);
      resetMasterForm();
      const res = await api.get('/admin/masters');
      setQuoteMasters(res.data || []);
    } catch (error) {
      toast.error('Failed to save');
    }
  };
  
  const handleDeleteMaster = async (id) => {
    if (!confirm('Delete this master?')) return;
    try {
      await api.delete(`/admin/masters/${id}`);
      setQuoteMasters(quoteMasters.filter(m => m.master_id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  // Room Tags handlers
  const resetTagForm = () => {
    setEditingTag(null);
    setTagForm({ tag_category: 'ROOM_VIEW', tag_name: '', tag_icon: '' });
  };
  
  const handleTagSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTag) {
        await api.put(`/admin/room-tags/${editingTag.tag_id}`, tagForm);
        toast.success('Updated');
      } else {
        await api.post('/admin/room-tags', tagForm);
        toast.success('Created');
      }
      setTagDialogOpen(false);
      resetTagForm();
      const res = await api.get('/admin/room-tags');
      setTags(res.data || []);
    } catch (error) {
      toast.error('Failed to save');
    }
  };
  
  const handleDeleteTag = async (id) => {
    if (!confirm('Delete this tag?')) return;
    try {
      await api.delete(`/admin/room-tags/${id}`);
      setTags(tags.filter(t => t.tag_id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  // Category Bands handlers
  const resetBandForm = () => {
    setEditingBand(null);
    setBandForm({ category_name: '', min_rate: '', max_rate: '', display_order: 0 });
  };
  
  const handleBandSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...bandForm,
        min_rate: parseFloat(bandForm.min_rate) || 0,
        max_rate: parseFloat(bandForm.max_rate) || 0,
      };
      if (editingBand) {
        await api.put(`/admin/category-bands/${editingBand.band_id}`, data);
        toast.success('Updated');
      } else {
        await api.post('/admin/category-bands', data);
        toast.success('Created');
      }
      setBandDialogOpen(false);
      resetBandForm();
      const res = await api.get('/admin/category-bands');
      setBands(res.data || []);
    } catch (error) {
      toast.error('Failed to save');
    }
  };
  
  const handleDeleteBand = async (id) => {
    if (!confirm('Delete this category band?')) return;
    try {
      await api.delete(`/admin/category-bands/${id}`);
      setBands(bands.filter(b => b.band_id !== id));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  const rateMasterCategories = [
    { key: 'roomTypes', label: 'Room Types', icon: BedDouble, description: 'Types of rooms like Standard, Deluxe, Suite' },
    { key: 'mealPlans', label: 'Meal Plans', icon: Utensils, description: 'Meal options like EP, CP, MAP, AP' },
    { key: 'bedTypes', label: 'Bed Types', icon: BedDouble, description: 'Bed configurations like King, Twin, Queen' },
    { key: 'viewTypes', label: 'View Types', icon: Eye, description: 'Room view options like Sea View, Garden View' },
    { key: 'features', label: 'Room Features', icon: Tag, description: 'Amenities like AC, TV, Balcony, WiFi' },
    { key: 'suppTypes', label: 'Supplement Types', icon: Activity, description: 'Extra charges like Extra Bed, Child Cost' },
    { key: 'ratePerTypes', label: 'Rate Per Types', icon: DollarSign, description: 'Rate units like Per Night, Per Person' },
    { key: 'vehicleTypes', label: 'Vehicle Types', icon: Car, description: 'Vehicle categories like Sedan, SUV, Tempo' },
    { key: 'transportTypes', label: 'Transport Types', icon: MapPin, description: 'Transfer modes like Arrival, Departure, Sightseeing' },
    { key: 'states', label: 'States', icon: Globe, description: 'Indian states and union territories' },
    { key: 'placeTypes', label: 'Place Types', icon: MapPin, description: 'Destination types like Hill Station, Beach, City' },
    { key: 'currencies', label: 'Currencies', icon: Coins, description: 'Currency codes like INR, USD, EUR' },
    { key: 'supplierTypes', label: 'Supplier Types', icon: Users, description: 'Vendor categories like Hotel, Transport, DMC' }
  ];
  
  const RateMasterList = ({ category, values }) => {
    const [newValue, setNewValue] = useState('');
    return (
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input 
            placeholder="Add new..."
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newValue.trim()) {
                handleAddRateMaster(category, newValue);
                setNewValue('');
              }
            }}
          />
          <Button size="sm" onClick={() => {
            if (newValue.trim()) {
              handleAddRateMaster(category, newValue);
              setNewValue('');
            }
          }}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
          {values.length === 0 ? (
            <p className="p-3 text-sm text-gray-500 text-center">No values</p>
          ) : (
            values.map((v, i) => (
              <div key={i} className="flex items-center justify-between p-2 hover:bg-gray-50">
                <span className="text-sm">{v}</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500"
                  onClick={() => handleDeleteRateMaster(category, v)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="space-y-6" data-testid="masters-manager">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-6 h-6" />
            Masters Management
          </h1>
          <p className="text-gray-500 mt-1">Manage all master data used across the system</p>
        </div>
        
        <Tabs defaultValue="rate-masters" className="w-full">
          <TabsList className="mb-4 flex-wrap">
            <TabsTrigger value="rate-masters">Hotel & Rate Masters</TabsTrigger>
            <TabsTrigger value="quote-masters">Quote Masters</TabsTrigger>
            <TabsTrigger value="room-tags">Room Tags</TabsTrigger>
            <TabsTrigger value="categories">Categories</TabsTrigger>
            <TabsTrigger value="vehicles">Vehicle Types</TabsTrigger>
          </TabsList>
          
          {/* Rate Masters Tab */}
          <TabsContent value="rate-masters">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {rateMasterCategories.map((cat) => (
                <Card key={cat.key}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <cat.icon className="w-4 h-4 text-blue-600" />
                      {cat.label}
                    </CardTitle>
                    {cat.description && (
                      <CardDescription className="text-xs">{cat.description}</CardDescription>
                    )}
                  </CardHeader>
                  <CardContent>
                    <RateMasterList category={cat.key} values={rateMasters[cat.key] || []} />
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
          
          {/* Quote Masters Tab */}
          <TabsContent value="quote-masters" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Quote Masters</h2>
              <Dialog open={masterDialogOpen} onOpenChange={setMasterDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetMasterForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Master
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="master-edit-dialog">
                  <DialogHeader>
                    <DialogTitle>{editingMaster ? 'Edit Master' : 'Add Master'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleMasterSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select value={masterForm.master_type} onValueChange={(v) => setMasterForm({ ...masterForm, master_type: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {QUOTE_MASTER_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Title (optional)</Label>
                      <Input value={masterForm.title} onChange={(e) => setMasterForm({ ...masterForm, title: e.target.value })} placeholder="Section title" />
                    </div>
                    <div className="space-y-2">
                      <Label>Content *</Label>
                      <RichContentEditor value={masterForm.content} onChange={(content) => setMasterForm((f) => ({ ...f, content }))} />
                      <p className="text-xs text-gray-500">Use the toolbar for lists and tables (e.g. cancellation charges). Tables show as tables in every quotation output.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox checked={masterForm.is_default} onCheckedChange={(c) => setMasterForm({ ...masterForm, is_default: c })} />
                      <Label>Include by default in new quotations</Label>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setMasterDialogOpen(false)}>Cancel</Button>
                      <Button type="submit">{editingMaster ? 'Update' : 'Create'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {QUOTE_MASTER_TYPES.map((type) => {
              const items = quoteMasters.filter(m => m.master_type === type.value);
              return (
                <Card key={type.value}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <type.icon className="w-4 h-4 text-purple-600" />
                      {type.label} ({items.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {items.length === 0 ? (
                      <p className="text-sm text-gray-500">No {type.label.toLowerCase()} configured</p>
                    ) : (
                      <div className="space-y-2">
                        {items.map((m) => (
                          <div key={m.master_id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              {m.title && <p className="font-medium text-sm">{m.title}</p>}
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">{m.content}</p>
                              {m.is_default && <Badge variant="secondary" className="mt-1 text-xs">Default</Badge>}
                            </div>
                            <div className="flex gap-1 ml-2">
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                                setEditingMaster(m);
                                setMasterForm({ ...m });
                                setMasterDialogOpen(true);
                              }}><Edit className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteMaster(m.master_id)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
          
          {/* Room Tags Tab */}
          <TabsContent value="room-tags" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Room Tags</h2>
              <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetTagForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Tag
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingTag ? 'Edit Tag' : 'Add Tag'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleTagSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <Select value={tagForm.tag_category} onValueChange={(v) => setTagForm({ ...tagForm, tag_category: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TAG_CATEGORIES.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tag Name *</Label>
                      <Input value={tagForm.tag_name} onChange={(e) => setTagForm({ ...tagForm, tag_name: e.target.value })} placeholder="e.g., Sea View" required />
                    </div>
                    <div className="space-y-2">
                      <Label>Icon (emoji or text)</Label>
                      <Input value={tagForm.tag_icon} onChange={(e) => setTagForm({ ...tagForm, tag_icon: e.target.value })} placeholder="e.g., 🌊" />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setTagDialogOpen(false)}>Cancel</Button>
                      <Button type="submit">{editingTag ? 'Update' : 'Create'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {TAG_CATEGORIES.map((cat) => {
              const catTags = tags.filter(t => t.tag_category === cat.value);
              return (
                <Card key={cat.value}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Tag className="w-4 h-4 text-green-600" />
                      {cat.label} ({catTags.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {catTags.length === 0 ? (
                      <p className="text-sm text-gray-500">No tags in this category</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {catTags.map((t) => (
                          <Badge key={t.tag_id} variant="outline" className="px-3 py-1 flex items-center gap-1">
                            {t.tag_icon && <span>{t.tag_icon}</span>}
                            {t.tag_name}
                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 ml-1" onClick={() => {
                              setEditingTag(t);
                              setTagForm({ ...t });
                              setTagDialogOpen(true);
                            }}><Edit className="w-3 h-3" /></Button>
                            <Button variant="ghost" size="sm" className="h-4 w-4 p-0 text-red-500" onClick={() => handleDeleteTag(t.tag_id)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>
          
          {/* Categories Tab */}
          <TabsContent value="categories" className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-lg font-semibold">Room Category Bands</h2>
                <p className="text-sm text-gray-500">Define price ranges for automatic room categorization</p>
              </div>
              <Dialog open={bandDialogOpen} onOpenChange={setBandDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetBandForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Category Band
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingBand ? 'Edit Category Band' : 'Add Category Band'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleBandSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name *</Label>
                      <Input value={bandForm.category_name} onChange={(e) => setBandForm({ ...bandForm, category_name: e.target.value })} placeholder="e.g., Budget, Standard, Luxury" required />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Min Rate</Label>
                        <Input type="number" value={bandForm.min_rate} onChange={(e) => setBandForm({ ...bandForm, min_rate: e.target.value })} placeholder="0" />
                      </div>
                      <div className="space-y-2">
                        <Label>Max Rate</Label>
                        <Input type="number" value={bandForm.max_rate} onChange={(e) => setBandForm({ ...bandForm, max_rate: e.target.value })} placeholder="5000" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Display Order</Label>
                      <Input type="number" value={bandForm.display_order} onChange={(e) => setBandForm({ ...bandForm, display_order: parseInt(e.target.value) || 0 })} />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setBandDialogOpen(false)}>Cancel</Button>
                      <Button type="submit">{editingBand ? 'Update' : 'Create'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            <Card>
              <CardContent className="pt-4">
                {bands.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No category bands configured</p>
                ) : (
                  <div className="space-y-2">
                    {bands.sort((a, b) => a.display_order - b.display_order).map((b) => (
                      <div key={b.band_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <Badge variant="outline" className="px-3">{b.category_name}</Badge>
                          <span className="text-sm text-gray-600">
                            Rate: {b.min_rate?.toLocaleString()} - {b.max_rate?.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => {
                            setEditingBand(b);
                            setBandForm({ ...b });
                            setBandDialogOpen(true);
                          }}><Edit className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteBand(b.band_id)}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Vehicle Types Tab */}
          <TabsContent value="vehicles" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  Vehicle Types
                </CardTitle>
                <CardDescription>
                  Define vehicle types with seating capacities. These are used in transfer rate calculations and quotation vehicle selection.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Car className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">
                    Manage your fleet of vehicle types with categories like Sedan, SUV, Tempo Traveller, etc.
                  </p>
                  <Button onClick={() => navigate(buildPath('/admin/vehicles'))}>
                    <Plus className="w-4 h-4 mr-2" />
                    Open Vehicle Master
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default MastersManager;
